import React, { useMemo, useState, createContext, Context } from 'react';
import { BeaconSubtitleContainer } from 'src/components/BeaconV2Components/BeaconChartWithLegend/BeaconSubtitleContainer';
import BeaconDoubleDropdownMetricColumnChart from 'src/components/BeaconV2Components/BeaconMetricColumnChart/BeaconDoubleDropdownMetricColumnChart';
import { GetAdMetricIndexName } from 'src/components/BeaconV2Components/ExperimentalLayout/Advertising/AdMetric/AdMetricsUtils';
import { AdvertisingMetric } from 'src/components/BeaconV2Components/ExperimentalLayout/Advertising/AdvertisingConstants';
import { formatDisplayName } from 'src/utils/stringFormatting';
import { useMainEntityType } from 'src/utils/Hooks/reduxSelectors';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import { useComparisonTimePeriodShortDisplayName, useAppSelector } from 'src/utils/Hooks';
import { ADVERTISING_EXPORT_TYPES } from './AdMetricGrid/AdMetricModal/utils';
import { checkInstacartRetailer, isBeaconCriteo } from 'src/utils/app';

const AdSpendField = {
  fieldName: AdvertisingMetric.AD_SPEND,
  label: 'Ad Spend'
};

const AdClickField = {
  fieldName: AdvertisingMetric.AD_CLICK,
  label: 'Ad Clicks'
};

const ImpressionsField = {
  fieldName: AdvertisingMetric.AD_IMPRESSION,
  label: 'Impressions'
};

const CTRField = {
  fieldName: AdvertisingMetric.CTR,
  label: 'CTR %'
};

const CPAField = {
  fieldName: AdvertisingMetric.CPA,
  label: 'CPA'
};

const CPMField = {
  fieldName: AdvertisingMetric.CPM,
  label: 'CPM'
};

const AdSalesField = {
  fieldName: AdvertisingMetric.AD_SALES,
  label: 'Ad Sales'
};

const Conversion = {
  fieldName: AdvertisingMetric.CONVERSION,
  label: 'Conversion'
};

const AdUnitSold = {
  fieldName: AdvertisingMetric.AD_UNITS_SOLD,
  label: 'Ad Units Sold'
};

const ROASField = {
  fieldName: AdvertisingMetric.ROAS,
  label: 'ROAS'
};

const entityGroupBy = {
  id: 'entityId',
  label: 'Entity',
  indexName: 'adCampaignDailyMetrics',
  entityType: 'adEntity'
};

const lineItemGroupBy = {
  id: ADVERTISING_EXPORT_TYPES.LINE_ITEM,
  label: 'Line Item',
  entityType: 'lineItem',
  indexName: 'advertisingDisplay'
};

const brandGroupBy = {
  id: 'brandId',
  label: 'Brand',
  indexName: 'adCampaignAdGroupProduct',
  entityType: 'brand'
};

const portfolioGroupBy = {
  id: 'portfolioId',
  label: 'Portfolio',
  indexName: 'adCampaignDailyMetrics',
  entityType: 'adPortfolio'
};

const campaignGroupBy = {
  id: 'campaignId',
  label: 'Campaign',
  indexName: 'adCampaignDailyMetrics',
  entityType: 'adCampaign'
};

const productGroupBy = {
  id: 'stacklineSku',
  label: 'Product',
  indexName: 'adCampaignAdGroupProductDailyMetrics',
  entityType: 'product'
};

const categoryGroupBy = {
  id: 'categoryId',
  label: 'Category',
  indexName: 'adCampaignAdGroupProductDailyMetrics',
  entityType: 'category'
};

const subCategoryGroupBy = {
  id: 'subCategoryId',
  label: 'Subcategory',
  indexName: 'adCampaignAdGroupProductDailyMetrics',
  entityType: 'subcategory'
};

const targetGroupBy = {
  id: 'targetingText',
  label: 'Target',
  indexName: 'adCampaignAdGroupTargetDailyMetrics',
  entityType: 'adTarget'
};

export const campaignTypeGroupBy = {
  id: 'campaignType',
  label: 'Campaign Type',
  indexName: 'adCampaignDailyMetrics',
  entityType: 'adEntity'
};

const metricColumnFields = [
  AdSpendField,
  AdClickField,
  ImpressionsField,
  CTRField,
  CPAField,
  CPMField,
  AdSalesField,
  Conversion,
  AdUnitSold,
  ROASField
];

const groupByFieldsByEntityType = {
  client: {
    options: [
      entityGroupBy,
      brandGroupBy,
      portfolioGroupBy,
      campaignGroupBy,
      targetGroupBy,
      productGroupBy,
      campaignTypeGroupBy,
      categoryGroupBy,
      subCategoryGroupBy
    ],
    default: campaignGroupBy
  },
  adEntity: {
    options: [
      brandGroupBy,
      portfolioGroupBy,
      campaignGroupBy,
      targetGroupBy,
      campaignTypeGroupBy,
      categoryGroupBy,
      subCategoryGroupBy,
      productGroupBy
    ],
    default: campaignGroupBy
  },
  adPortfolio: {
    options: [
      brandGroupBy,
      campaignGroupBy,
      targetGroupBy,
      campaignTypeGroupBy,
      categoryGroupBy,
      subCategoryGroupBy,
      productGroupBy
    ],
    default: campaignGroupBy
  },
  adCampaign: {
    options: [brandGroupBy, targetGroupBy, productGroupBy, categoryGroupBy, subCategoryGroupBy],
    default: targetGroupBy
  },
  brand: {
    options: [
      campaignGroupBy,
      entityGroupBy,
      portfolioGroupBy,
      productGroupBy,
      categoryGroupBy,
      subCategoryGroupBy,
      targetGroupBy
    ],
    default: productGroupBy
  },
  category: {
    options: [campaignGroupBy, entityGroupBy, portfolioGroupBy, productGroupBy, subCategoryGroupBy, targetGroupBy],
    default: productGroupBy
  },
  subcategory: {
    options: [campaignGroupBy, entityGroupBy, portfolioGroupBy, productGroupBy, targetGroupBy],
    default: productGroupBy
  },
  product: { options: [campaignGroupBy, targetGroupBy, campaignTypeGroupBy], default: targetGroupBy }
};

export const OverrideContext: Context<string[]> = createContext(null);

const AdMetricBarChart = () => {
  const entityType = useMainEntityType();
  const mainTimePeriod = useAppSelector((state) => state.mainTimePeriod);
  const comparisonDisplayName = useComparisonTimePeriodShortDisplayName();
  const [selectedGroupByField, setSelectedGroupByField] = useState(groupByFieldsByEntityType[entityType].default.id);
  const isCriteo = isBeaconCriteo();
  const isInstacart = checkInstacartRetailer();
  const groupByOptions = useMemo(() => {
    const base = { ...groupByFieldsByEntityType[entityType] };

    if (isCriteo && !base.options.some((option) => option.id === lineItemGroupBy.id)) {
      base.options = [base.options[0], lineItemGroupBy, ...base.options.slice(1)];
    }

    if (isInstacart) {
      base.options = base.options.filter((option) => option.id !== portfolioGroupBy.id);
    }

    return base;
  }, [entityType, isCriteo, isInstacart]);

  const selectedOption = groupByOptions.options.find((option) => option.id === selectedGroupByField);

  const getSubtitle = ({ mainMetricDisplayName }: { mainMetricDisplayName: string }) => {
    const preserveMetrics = ['ROAS', 'CPA', 'CPM', 'CPC', 'CTR'];
    const preserveRegex = new RegExp(`\\b(${preserveMetrics.join('|')})\\b`, 'i'); // Removed 'g' flag

    const transformString = (input) => {
      return input
        .split(/\s+/) // Split the string into words by whitespace
        .map(
          (word) => (preserveRegex.test(word) ? word : word.toLowerCase()) // Use test() to avoid cursor state issues
        )
        .join(' '); // Rejoin the words into a single string
    };

    const metricName = transformString(mainMetricDisplayName);
    return (
      <BeaconSubtitleContainer>
        <Text variant="body1">
          The chart displays the {metricName} by {selectedOption?.label.toLowerCase()} during the{' '}
          {formatDisplayName(mainTimePeriod)} period compared to the {comparisonDisplayName}.
        </Text>
      </BeaconSubtitleContainer>
    );
  };

  return (
    <OverrideContext.Provider value={['entity', 'campaign', 'product', 'target']}>
      <BeaconDoubleDropdownMetricColumnChart
        fields={metricColumnFields}
        groupByFields={groupByOptions.options.map((option) => {
          const indexName = GetAdMetricIndexName(entityType, option.entityType);
          return {
            id: option.id,
            label: option.label,
            indexName
          };
        })}
        selectedGroupByCallback={setSelectedGroupByField}
        selectedGroupByField={groupByOptions.default.id}
        useAdvancedSearchDataArgs={{
          requestBuilder: (builder) =>
            builder.removeConditionTermFilters((builderFilter) => builderFilter.fieldName === 'categoryId')
        }}
        beaconChartWithLegendPropsBuilder={(props, mainMetric) => {
          const mainMetricDisplayName = metricColumnFields.find((field) => field.fieldName === mainMetric)?.label ?? '';

          return {
            ...props,
            includeSummaryInfo: false,
            subtitle: getSubtitle({ mainMetricDisplayName })
          };
        }}
      />
    </OverrideContext.Provider>
  );
};

export default AdMetricBarChart;
